import React, { useState } from 'react';
import { ScrollView, TouchableOpacity, Image, Animated } from 'react-native';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { X, Star, User, PartyPopper } from 'lucide-react-native';
import { CustomButton } from '@/components';
import { LinearGradient } from '@/components/GradientView';
import { COLORS } from '@/constants/colors';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';


interface Participant {
  id: string;
  name: string;
  age: number;
  photoUri?: string;
}

const ParticipantRatingScreen: React.FC = () => {
  const { t } = useTranslation();
  const themeName = useThemeName();
  const navigation = useNavigation();
  const [currentParticipantIndex, setCurrentParticipantIndex] = useState(0);
  const [ratings, setRatings] = useState<{ [key: string]: number }>({});
  const [slideAnim] = useState(new Animated.Value(0));
  const [isAnimating, setIsAnimating] = useState(false);
  const [showRatingFeedback, setShowRatingFeedback] = useState(false);
  const [starAnim] = useState(new Animated.Value(1));
  const [showCompletionSlide, setShowCompletionSlide] = useState(false);
  
  const participants: Participant[] = [
    { id: '1', name: 'Marta', age: 25, photoUri: undefined },
    { id: '2', name: 'Krzysztof', age: 28, photoUri: undefined },
    { id: '3', name: 'Anna', age: 23, photoUri: undefined },
  ];

  const currentParticipant = participants[currentParticipantIndex];
  const currentRating = ratings[currentParticipant.id] || 0;
  const ratedCount = Object.keys(ratings).length;
  const totalCount = participants.length;

  const cardBackgroundColor = themeName === 'dark' ? '#1f1f1f' : '#ffffff';
  const borderColor = themeName === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
  const gradientColors = themeName === 'dark'
    ? [COLORS.DARK_GRADIENT_START, COLORS.DARK_GRADIENT_END]
    : [COLORS.LIGHT_GRADIENT_START, COLORS.LIGHT_GRADIENT_END];

  // Styles for inline elements
  const scrollViewContentStyle = { paddingBottom: 32, flexGrow: 1 };
  const gradientStyle = { borderBottomLeftRadius: 24, borderBottomRightRadius: 24 };
  const closeButtonStyle = {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0)',
    borderWidth: 1,
    borderColor: '#ffffff',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  };
  const imageStyle = { width: 36, height: 36, borderRadius: 9999 };
  const largeImageStyle = { width: 76, height: 76, borderRadius: 9999 };
  const starButtonStyle = { padding: 8, borderRadius: 20 };

  const slideToNext = () => {
    if (isAnimating || currentParticipantIndex >= participants.length - 1) return;
    
    setIsAnimating(true);
    Animated.sequence([
      Animated.timing(slideAnim, {
        toValue: 1,
        duration: 250,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 0,
        useNativeDriver: true,
      })
    ]).start(() => {
      setCurrentParticipantIndex(prev => prev + 1);
      setIsAnimating(false);
    });
  };


  const handleRatingChange = (rating: number) => {
    setRatings(prev => ({
      ...prev,
      [currentParticipant.id]: rating
    }));
    
    Animated.sequence([
      Animated.timing(starAnim, {
        toValue: 1.2,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(starAnim, {
        toValue: 1,
        duration: 150,
        useNativeDriver: true,
      })
    ]).start();
    
    setShowRatingFeedback(true);
    
    setTimeout(() => {
      if (currentParticipantIndex < participants.length - 1) {
        slideToNext();
        setShowRatingFeedback(false);
      } else {
        setShowCompletionSlide(true);
        setShowRatingFeedback(false);
      }
    }, 1500);
  };

  const handleGoToMain = () => {
    navigation.navigate('Home' as never);
  };



  return (
    <YStack flex={1} backgroundColor={themeName === 'dark' ? '#000000' : '#f5f5f5'} padding="$4">
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={scrollViewContentStyle}
      >
        <YStack
          backgroundColor={cardBackgroundColor}
          borderRadius="$6"
          marginTop="$8"
          elevation={8}
          shadowColor="rgba(0,0,0,0.15)"
          borderWidth={1}
          borderColor={borderColor}
          marginBottom="$4"
          overflow="hidden"
        >
          <LinearGradient
            colors={gradientColors}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={gradientStyle}
          >
            <YStack
              padding="$4"
              alignItems="center"
              space="$3"
              borderTopLeftRadius="$6"
              borderTopRightRadius="$6"
              overflow="hidden"
            >
               <XStack width="100%" justifyContent="flex-start">
                 <TouchableOpacity
                   style={closeButtonStyle}
                 >
                   <X size={16} color="#ffffff" />
                 </TouchableOpacity>
               </XStack>

               <YStack
                 width={130}
                 height={130}
                 backgroundColor="transparent"
                 alignItems="center"
                 justifyContent="center"
                 marginTop={-14}
               >
                 <PartyPopper size={68} color="#ffffff" strokeWidth={1.5} />
               </YStack>

               <Text fontSize={24} fontWeight="bold" fontFamily="Poppins" color="#ffffff" textAlign="center" marginTop={-6}>
                 {t('screens.participantRating.title')}
               </Text>

               <Text fontSize={14} color="#ffffff" textAlign="center" lineHeight={20}>
                 {t('screens.participantRating.subtitle')}
               </Text>

               <YStack width="100%" alignItems="center" space="$2" marginTop="$2">
                 <Text fontSize={12} color="rgba(255,255,255,0.8)" textAlign="center">
                   {t('screens.participantRating.progress', { rated: ratedCount, total: totalCount })}
                 </Text>
                 <YStack
                   width="80%"
                   height={4}
                   backgroundColor="rgba(255,255,255,0.2)"
                   borderRadius={2}
                   overflow="hidden"
                 >
                   <Animated.View
                     style={{
                       width: `${(ratedCount / totalCount) * 100}%`,
                       height: '100%',
                       backgroundColor: '#ffffff',
                       borderRadius: 2,
                     }}
                   />
                 </YStack>
               </YStack>
            </YStack>
          </LinearGradient>

          <YStack padding="$4" space="$4">
            <YStack
              backgroundColor={themeName === 'dark' ? '#181818' : '#ffffff'}
              borderRadius="$6"
              padding="$4"
              borderWidth={1}
              borderColor={borderColor}
              space="$3"
            >
              <Text fontSize={18} fontWeight="600" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                {t('screens.participantRating.meetingDetails')}
              </Text>

              <XStack alignItems="center" space="$3">
                <YStack
                  width={40}
                  height={40}
                  borderRadius={9999}
                  backgroundColor="$gray4"
                  borderWidth={2}
                  borderColor={themeName === 'dark' ? '#ffffff' : '#484848'}
                  alignItems="center"
                  justifyContent="center"
                >
                  {currentParticipant.photoUri ? (
                    <Image
                      source={{ uri: currentParticipant.photoUri }}
                      style={imageStyle}
                    />
                  ) : (
                    <User size={20} color={themeName === 'dark' ? '#ffffff' : '#484848'} />
                  )}
                </YStack>
                <Text fontSize={16} fontWeight="500" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                  Agata, Marta, Ja
                </Text>
              </XStack>

              <YStack space="$3">
                <XStack alignItems="center" space="$2">
                  <XStack space="$2" flexShrink={1}>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>{t('screens.participantRating.date')}</Text>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'} flexShrink={1}>27 lipca, 15:00</Text>
                  </XStack>
                </XStack>
                
                <XStack alignItems="center" space="$2">
                  <XStack space="$2" flexShrink={1}>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>{t('screens.participantRating.location')}</Text>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'} flexShrink={1}>Warszawa Mokotów</Text>
                  </XStack>
                </XStack>
                
                <XStack alignItems="center" space="$2">
                  <XStack space="$2">
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>{t('screens.participantRating.amount')}</Text>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>50,00 zł/os</Text>
                  </XStack>
                </XStack>
                
                <XStack alignItems="center" space="$2">
                  <XStack space="$2" flexShrink={1}>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>{t('screens.participantRating.type')}</Text>
                    <Text fontSize={14} fontWeight="normal" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'} flexShrink={1}>Gra w piłkę</Text>
                  </XStack>
                </XStack>
              </YStack>
            </YStack>

            {!showCompletionSlide ? (
              <YStack
                backgroundColor={themeName === 'dark' ? '#181818' : '#ffffff'}
                borderRadius="$6"
                padding="$4"
                borderWidth={1}
                borderColor={borderColor}
                space="$4"
              >
                <XStack justifyContent="space-between" alignItems="center">
                  <Text fontSize={18} fontWeight="600" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                    {t('screens.participantRating.rateParticipants')}
                  </Text>
                  <Text fontSize={14} color={themeName === 'dark' ? '#a1a1aa' : '#71717a'}>
                    {t('screens.participantRating.participantsRated', { rated: ratedCount, total: totalCount })}
                  </Text>
                </XStack>

               <Animated.View
                 style={{
                   transform: [{
                     translateX: slideAnim.interpolate({
                       inputRange: [-1, 0, 1],
                       outputRange: [-400, 0, 400],
                     })
                   }, {
                     scale: slideAnim.interpolate({
                       inputRange: [-1, 0, 1],
                       outputRange: [0.8, 1, 0.8],
                     })
                   }],
                   opacity: slideAnim.interpolate({
                     inputRange: [-1, 0, 1],
                     outputRange: [0.2, 1, 0.2],
                   })
                 }}
               >
                 <YStack
                   backgroundColor={themeName === 'dark' ? '#2a2a2a' : '#f9f9f9'}
                   borderRadius="$4"
                   padding="$4"
                   borderWidth={1}
                   borderColor={borderColor}
                   space="$4"
                   alignItems="center"
                 >
                <YStack alignItems="center" space="$3">
                  <YStack
                    width={80}
                    height={80}
                    borderRadius={9999}
                    backgroundColor="$gray4"
                    borderWidth={2}
                    borderColor={themeName === 'dark' ? '#ffffff' : '#484848'}
                    alignItems="center"
                    justifyContent="center"
                  >
                    {currentParticipant.photoUri ? (
                      <Image
                        source={{ uri: currentParticipant.photoUri }}
                        style={largeImageStyle}
                      />
                    ) : (
                      <Text fontSize={32} fontWeight="bold" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                        {currentParticipant.name.charAt(0)}
                      </Text>
                    )}
                  </YStack>

                  <Text fontSize={20} fontWeight="600" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                    {currentParticipant.name}
                  </Text>

                  <Text fontSize={16} fontWeight="500" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'} textAlign="center">
                    {t('screens.participantRating.ratePrompt')} {currentParticipant.name}?
                  </Text>
                </YStack>

                 <XStack space="$2" justifyContent="center" alignItems="center">
                   {[1, 2, 3, 4, 5].map((star) => {
                     const isActive = star <= currentRating;
                     const activeColor = '#fbbf24';
                     const inactiveColor = themeName === 'dark' ? '#4a4a4a' : '#d1d5db';
                     return (
                       <Animated.View
                         key={star}
                         style={{
                           transform: [{
                             scale: starAnim
                           }]
                         }}
                       >
                         <TouchableOpacity
                           onPress={() => handleRatingChange(star)}
                           onLongPress={() => handleRatingChange(0)}
                           accessibilityRole="button"
                           accessibilityLabel={t('screens.participantRating.ratingLabel', { count: star })}
                           style={starButtonStyle}
                         >
                           <Star
                             size={32}
                             color={isActive ? activeColor : inactiveColor}
                             fill={isActive ? activeColor : 'transparent'}
                             strokeWidth={isActive ? 0 : 1.5}
                           />
                         </TouchableOpacity>
                       </Animated.View>
                     );
                   })}
                 </XStack>

                 <YStack alignItems="center" space="$2">
                   <Text fontSize={12} color={themeName === 'dark' ? '#a1a1aa' : '#71717a'} textAlign="center">
                     {showRatingFeedback ? t('screens.participantRating.ratingSaved') : t('screens.participantRating.tapStar')}
                   </Text>
                   {showRatingFeedback && (
                     <Animated.View
                       style={{
                         opacity: slideAnim.interpolate({
                           inputRange: [0, 1],
                           outputRange: [1, 0],
                         })
                       }}
                     >
                       <YStack
                         backgroundColor="#22c55e"
                         paddingHorizontal="$3"
                         paddingVertical="$1"
                         borderRadius="$2"
                       >
                         <Text fontSize={10} color="white" fontWeight="600">
                           {t('screens.participantRating.ratingSavedShort')}
                         </Text>
                       </YStack>
                     </Animated.View>
                   )}
                 </YStack>

                <XStack space="$3" justifyContent="space-between" alignItems="center" width="100%">
                  <YStack borderWidth={1} borderColor="#ef4444" borderRadius="$3" flex={1} alignItems="stretch">
                    <CustomButton
                      backgroundColor="transparent"
                      color="#ef4444"
                      fontSize={14}
                      fontWeight="500"
                      paddingVertical="$3"
                      paddingHorizontal="$4"
                      borderRadius="$3"
                      textAlign="center"
                    >
                      {t('screens.participantRating.reportProblem')}
                    </CustomButton>
                  </YStack>
                  
                  <YStack borderWidth={1} borderColor="#5E809C" borderRadius="$3" flex={1} alignItems="stretch">
                    <CustomButton
                      backgroundColor="transparent"
                      color="#5E809C"
                      fontSize={14}
                      fontWeight="500"
                      paddingVertical="$3"
                      paddingHorizontal="$4"
                      borderRadius="$3"
                      textAlign="center"
                    >
                      {t('screens.participantRating.addToFavorites')}
                    </CustomButton>
                   </YStack>
                 </XStack>
                 </YStack>
               </Animated.View>
             </YStack>
             ) : (
               <YStack
                 backgroundColor={themeName === 'dark' ? '#181818' : '#ffffff'}
                 borderRadius="$6"
                 padding="$4"
                 borderWidth={1}
                 borderColor={borderColor}
                 space="$4"
                 alignItems="center"
               >
                 <XStack justifyContent="space-between" alignItems="center" width="100%">
                   <Text fontSize={18} fontWeight="600" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                     {t('screens.participantRating.rateParticipants')}
                   </Text>
                   <Text fontSize={14} color={themeName === 'dark' ? '#a1a1aa' : '#71717a'}>
                     {t('screens.participantRating.participantsRated', { rated: ratedCount, total: totalCount })}
                   </Text>
                 </XStack>

                 <Animated.View
                   style={{
                     transform: [{
                       translateX: slideAnim.interpolate({
                         inputRange: [-1, 0, 1],
                         outputRange: [-400, 0, 400],
                       })
                     }, {
                       scale: slideAnim.interpolate({
                         inputRange: [-1, 0, 1],
                         outputRange: [0.8, 1, 0.8],
                       })
                     }],
                     opacity: slideAnim.interpolate({
                       inputRange: [-1, 0, 1],
                       outputRange: [0.2, 1, 0.2],
                     })
                   }}
                 >
                   <YStack
                     backgroundColor={themeName === 'dark' ? '#2a2a2a' : '#f9f9f9'}
                     borderRadius="$4"
                     padding="$6"
                     borderWidth={1}
                     borderColor={borderColor}
                     space="$4"
                     alignItems="center"
                     minHeight={200}
                     justifyContent="center"
                   >
                     <YStack alignItems="center" space="$4">
                       <Text fontSize={48}>
                         🎉
                       </Text>

                       <Text fontSize={24} fontWeight="700" fontFamily="Poppins" color={themeName === 'dark' ? '#ffffff' : '#484848'} textAlign="center">
                         {t('screens.participantRating.allRated')}
                       </Text>

                       <Text fontSize={16} color={themeName === 'dark' ? '#a1a1aa' : '#71717a'} textAlign="center" lineHeight={22}>
                         {t('screens.participantRating.thankYouMessage')}
                       </Text>

                       <YStack alignItems="center" space="$2" marginTop="$2">
                         <Text fontSize={14} fontWeight="600" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                           {t('screens.participantRating.ratingSummary')}
                         </Text>
                         <XStack space="$2" flexWrap="wrap" justifyContent="center">
                           {participants.map((participant) => (
                             <YStack
                               key={participant.id}
                               backgroundColor={themeName === 'dark' ? '#3a3a3a' : '#f0f0f0'}
                               paddingHorizontal="$3"
                               paddingVertical="$2"
                               borderRadius="$2"
                               alignItems="center"
                               space="$1"
                             >
                               <Text fontSize={12} fontWeight="500" color={themeName === 'dark' ? '#ffffff' : '#484848'}>
                                 {participant.name}
                               </Text>
                               <XStack space="$1">
                                 {[1, 2, 3, 4, 5].map((star) => (
                                   <Star
                                     key={star}
                                     size={12}
                                     color={star <= (ratings[participant.id] || 0) ? '#fbbf24' : (themeName === 'dark' ? '#4a4a4a' : '#d1d5db')}
                                     fill={star <= (ratings[participant.id] || 0) ? '#fbbf24' : 'transparent'}
                                   />
                                 ))}
                               </XStack>
                             </YStack>
                           ))}
                         </XStack>
                       </YStack>
                     </YStack>
                   </YStack>
                 </Animated.View>
               </YStack>
             )}

             <CustomButton
               backgroundColor="#5E809C"
               color="white"
               fontSize={16}
               fontWeight="600"
               paddingVertical="$3"
               borderRadius="$3"
               onPress={handleGoToMain}
             >
               {showCompletionSlide ? t('screens.participantRating.finishAndReturn') : t('screens.participantRating.backToMain')}
             </CustomButton>
          </YStack>
        </YStack>
      </ScrollView>
    </YStack>
  );
};

export default ParticipantRatingScreen;
